import { ParticipantResolutionSelect } from './ParticipantResolutionSelect';
import { MeetingDeviceSettingsDialog } from './MeetingDeviceSettingsDialog';
import { AddMeetingDevicesDialog } from './AddMeetingDevicesDialog';
import { MeetingMicrophoneVolume } from './MeetingMicrophoneVolume';
import { MeetingLayoutPicker } from '@/modules/meetings/components/MeetingLayoutPicker';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { formatDateUTC7 } from "@/lib/date.utils";
import { updateMeeting } from "@/modules/meetings/api/api-meeting";
import {
  CreateMeetingPayload,
  MeetingDevice,
  MeetingParticipant,
} from "@/modules/meetings/types/meeting.type";
import { Participant, Track } from "livekit-client";
import { LogOut, Mic, MicOff, Monitor, Search, VideoOff, Hand, Crown, ScreenShare, ScreenShareOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "ts-debounce";
import { muteAllParticipantMeeting, updateParticipantMeeting, updateRaiseHandMeeting } from "../api/api-setting-meeting";
import { useLiveKit } from "../contexts/LiveKitContext";
import { cn } from "@/lib/utils";
import { updateVolume } from "@/modules/device/api/device.api";
import { DeviceApi } from "@/modules/device/types/device.type";
import { toast } from "sonner";
import { ApiError } from "@/api/api-axios";

interface ControlPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MergedParticipant {
  id: string;
  identity: string;
  name: string;
  fullName: string;
  email?: string;
  type: "user" | "device";
  role?: string;
  isConnected: boolean;
  canPublish?: boolean;
  canSubscribe?: boolean;
  micMuted?: boolean;
  cameraMuted?: boolean;
  livekitParticipant?: Participant;
  meetingParticipant?: MeetingParticipant;
  meetingDevice?: MeetingDevice;
  connectionStatus?: string;
  joinedAt?: string | null;
  leftAt?: string | null;
  deviceImei?: string | null;
  device?: DeviceApi;
  raisedHand?: boolean;
  isCoHost?: boolean;
  canShareScreen?: boolean;
}

export function ControlPanel({ isOpen, onClose }: ControlPanelProps) {
  const {
    meeting,
    room,
    muteParticipant,
    unmuteParticipant,
    disableParticipantCamera,
    enableParticipantCamera,
    removeParticipant,
    refreshMeeting,
    isHostOrCoHost,
    currentUserRole,
  } = useLiveKit();
  const [muteAllMic, setMuteAllMic] = useState(false);
  const [muteAllCamera, setMuteAllCamera] = useState(false);
  const [lockUserActions, setLockUserActions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [volumeStates, setVolumeStates] = useState<Record<string, number>>({});
  const itemsPerPage = 24;

  useEffect(() => {
    if (isOpen) {
      refreshMeeting();
      setVolumeStates({});
    }
  }, [isOpen, refreshMeeting]);

  useEffect(() => {
    if (meeting?.lockAction !== undefined) {
      setLockUserActions(meeting.lockAction);
    }
  }, [meeting?.lockAction]);

  // Merge tất cả users và devices với remoteParticipants
  const allMergedParticipants = useMemo(() => {
    const merged: MergedParticipant[] = [];

    if (!meeting) return merged;

    // Xử lý users từ meeting participants
    meeting.participants.forEach((meetingParticipant) => {
      const userId = meetingParticipant.userId;
      const identity = meetingParticipant.identity;
      // Tìm livekitParticipant bằng userId hoặc identity để đảm bảo matching đúng
      let livekitParticipant: Participant | undefined;
      if (
        userId === room.localParticipant?.identity ||
        identity === room.localParticipant?.identity
      ) {
        livekitParticipant = room.localParticipant;
      } else {
        livekitParticipant =
          room.remoteParticipants.get(userId) ||
          room.remoteParticipants.get(identity);
      }

      merged.push({
        id: meetingParticipant.id,
        identity: identity,
        name: meetingParticipant.name,
        fullName: meetingParticipant.fullName,
        email: meetingParticipant.email,
        type: meetingParticipant.deviceImei !== null ? "device" : "user",
        role: meetingParticipant.role,
        isConnected: !!livekitParticipant,
        canPublish: meetingParticipant.canPublish ?? false,
        canSubscribe: meetingParticipant.canSubscribe ?? false,
        micMuted: meetingParticipant.micMuted ?? false,
        cameraMuted: meetingParticipant.cameraMuted ?? false,
        livekitParticipant: livekitParticipant ?? undefined,
        meetingParticipant,
        joinedAt: meetingParticipant.joinedAt,
        leftAt: meetingParticipant.leftAt,
        deviceImei: meetingParticipant.deviceImei,
        device: meetingParticipant.device,
        raisedHand: meetingParticipant.isRaiseHand ?? false,
        isCoHost: meetingParticipant.isCoHost ?? false,
        canShareScreen: meetingParticipant.canShared ?? false,
      });
    });

    // Added devices are visible immediately, including devices that have not joined yet.
    for (const device of meeting.devices ?? []) {
      if (merged.some(p => p.deviceImei === device.imei)) continue;
      merged.push({id: `meeting-device-${device.deviceId}`, identity: device.imei,
        name: device.deviceName, fullName: device.deviceName, type: 'device',
        isConnected: false, deviceImei: device.imei, meetingDevice: device,
        canPublish: true, canSubscribe: true, joinedAt: null, leftAt: null});
    }
    return merged;
  }, [meeting, room]);

  const filteredParticipants = useMemo(() => {
    if (!searchQuery.trim()) return allMergedParticipants;
    const query = searchQuery.toLowerCase();
    return allMergedParticipants.filter(
      (p) =>
        p.name?.toLowerCase().includes(query) ||
        p.identity?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query)
    );
  }, [allMergedParticipants, searchQuery]);

  const paginatedParticipants = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredParticipants.slice(start, end);
  }, [filteredParticipants, currentPage, itemsPerPage]);
  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

  const handleMuteParticipant = useCallback(
    async (participant: MergedParticipant) => {
      if (!participant.canPublish) return;
      try {
        await muteParticipant(participant.identity);
        await refreshMeeting();
      } catch (error) {
        console.error("Error muting participant:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [muteParticipant, refreshMeeting]
  );

  const handleUnmuteParticipant = useCallback(
    async (participant: MergedParticipant) => {
      if (!participant.canPublish) return;
      try {
        await unmuteParticipant(participant.identity);
        await refreshMeeting();
      } catch (error) {
        console.error("Error unmuting participant:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [unmuteParticipant, refreshMeeting]
  );
  const handleMuteAllParticipant = useCallback(
    async (checked: boolean) => {
      if (!meeting?.id) return;

      try {
        setMuteAllMic(checked);
        await muteAllParticipantMeeting(meeting.id, {
          trackType: Track.Kind.Audio,
          muted: checked,
        });
        await refreshMeeting();
      } catch (error) {
        console.error("Error muting all participants:", error);
        setMuteAllMic(!checked);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [meeting, refreshMeeting]
  );

  const handleMuteAllCamera = useCallback(
    async (checked: boolean) => {
      if (!meeting?.id) return;

      try {
        setMuteAllCamera(checked);
        await muteAllParticipantMeeting(meeting.id, {
          trackType: Track.Kind.Video,
          muted: checked,
        });
        await refreshMeeting();
      } catch (error) {
        console.error("Error muting all cameras:", error);
        setMuteAllCamera(!checked);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [meeting, refreshMeeting]
  );

  const handleDisableCamera = useCallback(
    async (participant: MergedParticipant) => {
      if (!participant.canSubscribe) return;
      try {
        await disableParticipantCamera(participant.identity);
        await refreshMeeting();
      } catch (error) {
        console.error("Error disabling camera:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [disableParticipantCamera, refreshMeeting]
  );

  const handleEnableCamera = useCallback(
    async (participant: MergedParticipant) => {
      if (!participant.canSubscribe) return;
      try {
        await enableParticipantCamera(participant.identity);
        await refreshMeeting();
      } catch (error) {
        console.error("Error enabling camera:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [enableParticipantCamera, refreshMeeting]
  );

  const handleRemoveParticipant = useCallback(
    async (participant: MergedParticipant) => {
      if (!participant.identity) return;
      try {
        await removeParticipant(participant.identity);
        await refreshMeeting();
      } catch (error) {
        console.error("Error removing participant:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [removeParticipant, refreshMeeting]
  );

  const onSubmitVolume = useCallback(
    async (deviceImei: string, values: string) => {
      if (!deviceImei) return;
      try {
        await updateVolume(deviceImei, parseInt(values));
        await refreshMeeting();
        setVolumeStates((prev) => {
          const newState = { ...prev };
          delete newState[deviceImei];
          return newState;
        });
      } catch (error) {
        console.error("Error setting participant volume:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
        // Reset volume state về giá trị cũ nếu có lỗi
        setVolumeStates((prev) => {
          const newState = { ...prev };
          delete newState[deviceImei];
          return newState;
        });
      }
    },
    [refreshMeeting]
  );
  const debounceOnSubmitVolume = useMemo(
    () =>
      debounce((deviceImei: string, values: string) => {
        onSubmitVolume(deviceImei, values);
      }, 500),
    [onSubmitVolume]
  );

  const handleVolumeChange = useCallback(
    (deviceImei: string, value: number) => {
      setVolumeStates((prev) => ({
        ...prev,
        [deviceImei]: value,
      }));
      // Gọi API với debounce
      debounceOnSubmitVolume(deviceImei, value.toString());
    },
    [debounceOnSubmitVolume]
  );

  const getRoleDisplay = useCallback((participant: MergedParticipant) => {
    if (participant.type === "device") {
      return (
        <span className=" truncate text-xs border border-orange-700/50 bg-orange-50 rounded-full px-2 py-0.5 text-orange-700">
          Thiết bị
        </span>
      );
    }
    if (participant.role === "host") {
      return (
        <span className=" truncate text-xs border border-green-700/50 bg-green-50 rounded-full px-2 py-0.5 text-green-700">
          Quản trị viên
        </span>
      );
    }
    if (participant.isCoHost === true) {
      return (
        <span className=" truncate text-xs border border-blue-700/50 bg-blue-50 rounded-full px-2 py-0.5 text-blue-700">
          Đồng chủ trì
        </span>
      );
    }
    if (participant.meetingParticipant?.metadata?.userId === null) {
      return (
        <span className=" truncate text-xs border border-gray-700/50 bg-gray-50 rounded-full px-2 py-0.5 text-gray-700">
          Chưa đăng nhập
        </span>
      );
    }else{
      return (
        <span className=" truncate text-xs border border-gray-700/50 bg-gray-50 rounded-full px-2 py-0.5 text-gray-700">
          Người dùng
        </span>
      );
    }
  }, []);

  const getConnectionStatus = useCallback((participant: MergedParticipant) => {
    // Có joinedAt và không có leftAt thì là đang tham gia
    if (participant.joinedAt && participant.leftAt === null) {
      return "Đang tham gia";
    }
    // Nếu có leftAt thì hiển thị thời gian rời
    if (participant.leftAt) {
      return formatDateUTC7(participant.leftAt, "relative", "vi-VN");
    }
    // Trường hợp khác (chưa join hoặc không có thông tin)
    return "Chưa tham gia";
  }, []);

  const handleLockUserActions = useCallback(
    async (checked: boolean) => {
      if (!meeting?.id) return;
      try {
        await updateMeeting(meeting.id, {
          lockAction: checked,
        } as Partial<CreateMeetingPayload>);
        setLockUserActions(checked);
        await refreshMeeting();
      } catch (error) {
        console.error("Error locking user actions:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [meeting?.id, refreshMeeting]
  );

  // Handler cho Raise Hand
  const handleRaiseHand = useCallback(
    async (participant: MergedParticipant, action: "add" | "remove") => {
      if (!meeting?.id) return;
      try {
        const payload = {
          [action === "add" ? "addRaiseHand" : "removeRaiseHand"]: [
            participant.identity,
          ],
        };
        await updateRaiseHandMeeting(meeting.id, payload);
        await refreshMeeting();
      
      } catch (error) {
        console.error("Error updating raise hand:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);

      }
    },
    [meeting?.id, refreshMeeting]
  );

  // Handler để hạ tất cả tay
  const handleClearAllRaiseHand = useCallback(async () => {
    if (!meeting?.id) return;
    try {
      await updateParticipantMeeting(meeting.id, {
        clearAllRaiseHand: true,
      });
      await refreshMeeting();
    } catch (error) {
      console.error("Error clearing all raise hands:", error);
      toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
    }
  }, [meeting?.id, refreshMeeting]);

  // Handler cho Co-Host
  const handleCoHost = useCallback(
    async (participant: MergedParticipant, action: "add" | "remove") => {
      if (!meeting?.id) return;
      try {
        const payload = {
          [action === "add" ? "addCoHost" : "removeCoHost"]: [
            participant.identity,
          ],
        };
        await updateParticipantMeeting(meeting.id, payload);
        await refreshMeeting();
        toast.success(
          action === "add"
            ? "Đã cấp quyền đồng chủ trì"
            : "Đã thu hồi quyền đồng chủ trì"
        );
      } catch (error) {
        console.error("Error updating co-host:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [meeting?.id, refreshMeeting]
  );

  // Handler cho Can Share Screen
  const handleCanShareScreen = useCallback(
    async (participant: MergedParticipant, action: "add" | "remove") => {
      if (!meeting?.id) return;
      if (action === "add" && meeting.allowScreenShare === false) {
        toast.error('Hãy bật "Cho phép người tham gia chia sẻ màn hình" trong cài đặt chung trước.');
        return;
      }
      try {
        const payload = {
          [action === "add" ? "addCanShare" : "removeCanShare"]: [
            participant.identity,
          ],
        };
        await updateParticipantMeeting(meeting.id, payload);
        await refreshMeeting();
        toast.success(
          action === "add"
            ? "Đã cho phép chia sẻ màn hình"
            : "Đã cấm chia sẻ màn hình"
        );
      } catch (error) {
        console.error("Error updating can share screen:", error);
        toast.error(`Be: ${(error as ApiError)?.data?.error?.message ?? ""}`);
      }
    },
    [meeting?.id, meeting?.allowScreenShare, refreshMeeting]
  );

  // Handler để toggle cho phép/cấm tất cả chia sẻ màn hình
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="mx-2 sm:mx-4 w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] md:max-w-[90vw] lg:max-w-[85vw] xl:max-w-[80vw] max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6">
        <DialogHeader className="pb-2 sm:pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base sm:text-lg">
              Bảng điều khiển
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6">
          {isHostOrCoHost && meeting && (
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 [&>button]:m-0 [&>button]:h-10">
              <MeetingLayoutPicker
                layout={meeting.layout}
                autoLayoutByMic={meeting.autoLayoutByMic}
                onSave={async (layout, autoLayoutByMic) => {
                  try {
                    await updateMeeting(meeting.id, { layout, autoLayoutByMic });
                    await refreshMeeting();
                    toast.success("Đã cập nhật bố cục cho mọi người");
                  } catch (error) {
                    toast.error("Không cập nhật được bố cục");
                    throw error;
                  }
                }}
              />
              <AddMeetingDevicesDialog
                meetingId={meeting.id}
                onAdded={refreshMeeting}
              />
            </div>
          )}
          {/* Cài đặt chung */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-base sm:text-lg">
              Cài đặt chung
            </h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <Label
                  htmlFor="mute-all-mic"
                  className="cursor-pointer text-sm sm:text-base flex-1"
                >
                  Tắt mic tất cả mọi người
                </Label>
                <Switch
                  id="mute-all-mic"
                  disabled={!isHostOrCoHost}
                  checked={muteAllMic}
                  onCheckedChange={handleMuteAllParticipant}
                />
              </div>
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <Label
                  htmlFor="mute-all-camera"
                  className="cursor-pointer text-sm sm:text-base flex-1"
                >
                  Tắt camera tất cả mọi người
                </Label>
                <Switch
                  id="mute-all-camera"
                  disabled={!isHostOrCoHost}
                  checked={muteAllCamera}
                  onCheckedChange={handleMuteAllCamera}
                />
              </div>
              <div className="flex items-center justify-between gap-2 sm:gap-4">
                <Label
                  htmlFor="lock-actions"
                  className="cursor-pointer text-sm sm:text-base flex-1"
                >
                  Khóa thao tác của người dùng
                </Label>
                <Switch
                  id="lock-actions"
                  disabled={!isHostOrCoHost}
                  checked={lockUserActions}
                  onCheckedChange={(checked: boolean) => {
                    handleLockUserActions(checked as boolean);
                  }}
                />
              </div>
              {isHostOrCoHost && <div className="flex items-center justify-between gap-4">
                <Label htmlFor="allow-screen-share" className="flex-1">Cho phép người tham gia chia sẻ màn hình</Label>
                <Switch id="allow-screen-share" checked={meeting?.allowScreenShare ?? true} onCheckedChange={async allowScreenShare => {
                  if (!meeting) return;
                  try { await updateMeeting(meeting.id, {allowScreenShare}); await refreshMeeting(); }
                  catch { toast.error('Không cập nhật được quyền chia sẻ màn hình'); }
                }}/>
              </div>}
              {/* Clear All Raise Hand Button */}
              {isHostOrCoHost && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={handleClearAllRaiseHand}
                  aria-label="Hạ tất cả tay"
                >
                  <Hand className="size-4 mr-2" />
                  Hạ tất cả tay
                </Button>
              )}
            </div>
          </div>

          {/* Tìm kiếm */}
          <div className="relative">
            <Search className="absolute left-2 sm:left-3 top-1/2 size-3 sm:size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Tìm tên người tham gia, thiết bị hoặc email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-7 sm:pl-10 text-sm sm:text-base h-9 sm:h-10"
              aria-label="Tìm kiếm người tham gia và thiết bị"
            />
          </div>

          {/* Danh sách người tham gia và thiết bị */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-base sm:text-lg">
              Danh sách người tham gia và thiết bị (
              {filteredParticipants.length})
            </h3>

            {/* Desktop Table View */}
            <div className="hidden md:block border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 lg:px-4 py-2 lg:py-3 text-left text-xs sm:text-sm font-medium">
                        Tên
                      </th>
                      <th className="px-3 lg:px-4 py-2 lg:py-3 text-left text-xs sm:text-sm font-medium">
                        Vai Trò
                      </th>
                      <th className="px-3 lg:px-4 py-2 lg:py-3 text-left text-xs sm:text-sm font-medium">
                        Trạng thái
                      </th>
                      <th className="px-3 lg:px-4 py-2 lg:py-3 text-left text-xs sm:text-sm font-medium">
                        Âm lượng loa
                      </th>
                      <th className="px-3 lg:px-4 py-2 lg:py-3 text-left text-xs sm:text-sm font-medium">Âm lượng MIC</th>
                      {isHostOrCoHost && (
                        <th className="px-3 lg:px-4 py-2 lg:py-3 text-left text-xs sm:text-sm font-medium">
                          Thao Tác
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParticipants.map((participant) => {
                      const isConnected = participant.joinedAt && participant.leftAt === null;
                      const isMicEnabled =
                        participant.livekitParticipant?.isMicrophoneEnabled ??
                        false;
                      const isCameraEnabled =
                        participant.livekitParticipant?.isCameraEnabled ??
                        false;

                      return (
                        <tr
                          key={`${participant.type}-${participant.id}`}
                          className="border-t hover:bg-muted/50"
                        >
                          <td className="px-3 lg:px-4 py-2 lg:py-3">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <Avatar className="size-7 sm:size-8">
                                <AvatarFallback className="text-xs sm:text-sm">
                                  {participant.type === "device" ? (
                                    <Monitor className="size-3 sm:size-4" />
                                  ) : (
                                    participant?.name
                                      ?.charAt(0)
                                      ?.toUpperCase() || "U"
                                  )}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm sm:text-base truncate">
                                  {participant?.name ||
                                    participant?.fullName || participant?.identity ||
                                    "Unknown"}
                                </div>
                                {participant.email && (
                                  <div className="text-xs sm:text-sm text-muted-foreground truncate">
                                    {participant.email}
                                  </div>
                                )}
                                {participant.deviceImei && (
                                  <div className="text-xs sm:text-sm text-muted-foreground truncate">
                                    IMEI: {participant.deviceImei}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 lg:px-4 py-2 lg:py-3">
                            {getRoleDisplay(participant)}
                          </td>
                          <td className="px-3 lg:px-4 py-2 lg:py-3">
                            <div className="flex items-center gap-2">
                              <div
                                className={`size-2 rounded-full ${isConnected ? "bg-green-500" : "bg-gray-400"
                                  }`}
                              />
                              <span className="text-xs sm:text-sm flex-1">
                                {getConnectionStatus(participant)}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 lg:px-4 py-2 lg:py-3">
                            {participant.type === "device" && participant.device?.speakerVolume !== undefined ? (
                              <div className="flex items-center gap-2 w-28 lg:w-32">
                                <Slider
                                  value={[
                                    volumeStates[participant.deviceImei || ""] ??
                                    participant.device?.speakerVolume ??
                                    0
                                  ]}
                                  max={100}
                          disabled={!isHostOrCoHost}
                                  className="flex-1"
                                  onValueChange={(value) =>
                                    handleVolumeChange(
                                      participant.deviceImei || "",
                                      value[0]
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <span className="text-xs sm:text-sm text-muted-foreground">
                                -
                              </span>
                            )}
                          </td>
                          <td className="px-3 lg:px-4 py-2 lg:py-3">
                            {meeting && participant.deviceImei ? <MeetingMicrophoneVolume
                              meetingId={meeting.id} imei={participant.deviceImei}
                              name={participant.name || participant.identity}
                              volume={participant.device?.microphoneVolume ?? participant.meetingDevice?.microphoneVolume ?? 100}
                              disabled={!isHostOrCoHost || !isConnected} onSaved={refreshMeeting}
                            /> : <span className="text-muted-foreground">—</span>}
                          </td>
                          {isHostOrCoHost && (
                            <td className="px-3 lg:px-4 py-2 lg:py-3 w-fit">
                              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                {meeting && !participant.deviceImei && <ParticipantResolutionSelect identity={participant.identity} disabled={!isConnected}/>}
                                {meeting && participant.deviceImei && <MeetingDeviceSettingsDialog
                                  meetingId={meeting.id} imei={participant.deviceImei} name={participant.name || participant.identity}
                                  disabled={!isHostOrCoHost || !isConnected} onSaved={refreshMeeting}/>}
                                {isConnected && participant.role !== "host" && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      className={cn(
                                        "h-7 sm:h-8 px-2 text-xs sm:text-sm",
                                        isMicEnabled
                                          ? "border-green-700/50 bg-green-50 text-green-700"
                                          : "border-gray-700/50 bg-gray-50 text-gray-700"
                                      )}
                                      size="sm"
                                      onClick={() =>
                                        isMicEnabled
                                          ? handleMuteParticipant(participant)
                                          : handleUnmuteParticipant(participant)
                                      }
                                      aria-label={
                                        isMicEnabled ? "Tắt mic" : "Bật mic"
                                      }
                                      disabled={!isHostOrCoHost}
                                    >
                                      {isMicEnabled ? (
                                        <>
                                          <Mic className="size-3 sm:size-4 text-green-500" />
                                          <span className="hidden lg:inline ml-1">
                                            Đã bật mic
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <MicOff className="size-3 sm:size-4 text-gray-500" />
                                          <span className="hidden lg:inline ml-1">
                                            Đã tắt mic
                                          </span>
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      className={cn(
                                        "h-7 sm:h-8 px-2 text-xs sm:text-sm",
                                        isCameraEnabled
                                          ? "border-green-700/50 bg-green-50 text-green-700"
                                          : "border-gray-700/50 bg-gray-50 text-gray-700"
                                      )}
                                      size="sm"
                                      onClick={() =>
                                        isCameraEnabled
                                          ? handleDisableCamera(participant)
                                          : handleEnableCamera(participant)
                                      }
                                      aria-label={
                                        isCameraEnabled
                                          ? "Tắt camera"
                                          : "Bật camera"
                                      }
                                      disabled={!isHostOrCoHost}
                                    >
                                      {isCameraEnabled ? (
                                        <>
                                          <Monitor className="size-3 sm:size-4 text-green-500" />
                                          <span className="hidden lg:inline ml-1">
                                            Đã bật camera
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <VideoOff className="size-3 sm:size-4 text-gray-500" />
                                          <span className="hidden lg:inline ml-1">
                                            Đã tắt camera
                                          </span>
                                        </>
                                      )}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      className={cn(
                                        "h-7 sm:h-8 px-2 text-xs sm:text-sm border-gray-700/50 bg-gray-50 text-gray-700"
                                      )}
                                      size="sm"
                                      onClick={() =>
                                        handleRemoveParticipant(participant)
                                      }
                                      aria-label="Xóa khỏi cuộc họp"
                                      disabled={!isHostOrCoHost}
                                    >
                                      <LogOut className="size-3 sm:size-4" />
                                      <span className="hidden lg:inline ml-1">
                                        Rời khỏi cuộc họp
                                      </span>
                                    </Button>
                                    {/* Raise Hand Button */}

                                    <Button
                                      variant="ghost"
                                      className={cn(
                                        "h-7 sm:h-8 px-2 text-xs sm:text-sm",
                                        participant.raisedHand
                                          ? "border-blue-700/50 bg-blue-50 text-blue-700"
                                          : "border-gray-700/50 bg-gray-50 text-gray-700"
                                      )}
                                      size="sm"
                                      onClick={() =>
                                        handleRaiseHand(
                                          participant,
                                          participant.raisedHand
                                            ? "remove"
                                            : "add"
                                        )
                                      }
                                      aria-label={
                                        participant.raisedHand
                                          ? "Hạ tay"
                                          : "Giơ tay"
                                      }
                                      disabled={!isHostOrCoHost}
                                    >
                                      <Hand className="size-3 sm:size-4" />
                                      <span className="hidden lg:inline ml-1">
                                        {participant.raisedHand
                                          ? "Hạ tay"
                                          : "Giơ tay"}
                                      </span>
                                    </Button>

                                    {/* Co-Host Button - Chỉ host mới có thể cấp/thu hồi quyền co-host */}
                                    {
                                      participant.role !== "host" &&
                                      currentUserRole === "host" && participant.meetingParticipant?.metadata?.userId !== null && (
                                        <Button
                                          variant="ghost"
                                          className={cn(
                                            "h-7 sm:h-8 px-2 text-xs sm:text-sm",
                                            participant.isCoHost
                                              ? "border-yellow-700/50 bg-yellow-50 text-yellow-700"
                                              : "border-gray-700/50 bg-gray-50 text-gray-700"
                                          )}
                                          size="sm"
                                          onClick={() =>
                                            handleCoHost(
                                              participant,
                                              participant.isCoHost
                                                ? "remove"
                                                : "add"
                                            )
                                          }
                                          aria-label={
                                            participant.isCoHost
                                              ? "Thu hồi quyền đồng chủ trì"
                                              : "Cấp quyền đồng chủ trì"
                                          }
                                          disabled={!isHostOrCoHost}
                                        >
                                          <Crown className="size-3 sm:size-4" />
                                          <span className="hidden lg:inline ml-1">
                                            {participant.isCoHost
                                              ? "Bỏ đồng chủ trì"
                                              : "Đồng chủ trì"}
                                          </span>
                                        </Button>
                                      )}
                                    {/* Can Share Screen Button */}
                                    {participant.type === "user" && (
                                      <Button
                                        variant="ghost"
                                        className={cn(
                                          "h-7 sm:h-8 px-2 text-xs sm:text-sm",
                                          participant.canShareScreen
                                            ? "border-green-700/50 bg-green-50 text-green-700"
                                            : "border-gray-700/50 bg-gray-50 text-gray-700"
                                        )}
                                        size="sm"
                                        onClick={() =>
                                          handleCanShareScreen(
                                            participant,
                                            participant.canShareScreen
                                              ? "remove"
                                              : "add"
                                          )
                                        }
                                        aria-label={
                                          participant.canShareScreen
                                            ? "Cấm chia sẻ màn hình"
                                            : "Cho phép chia sẻ màn hình"
                                        }
                                        disabled={!isHostOrCoHost}
                                      >
                                        {participant.canShareScreen
                                          ? <>
                                            <ScreenShare className="size-3.5 mr-1" />
                                            Có thể chia sẻ
                                          </>
                                          :
                                          <>
                                            <ScreenShareOff className="size-3.5 mr-1" />
                                            Cấm chia sẻ màn hình
                                          </>
                                        }
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {paginatedParticipants.map((participant) => {
                const isConnected = participant.joinedAt && participant.leftAt === null;
                const isMicEnabled =
                  participant.livekitParticipant?.isMicrophoneEnabled ?? false;
                const isCameraEnabled =
                  participant.livekitParticipant?.isCameraEnabled ?? false;

                return (
                  <div
                    key={`${participant.type}-${participant.id}`}
                    className="border rounded-lg p-3 space-y-3 bg-card"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="size-10">
                        <AvatarFallback>
                          {participant.type === "device" ? (
                            <Monitor className="size-5" />
                          ) : (
                            participant?.name?.charAt(0)?.toUpperCase() || "U"
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {participant?.name ||
                            participant?.fullName ||
                            "Unknown"}
                        </div>
                        {participant.email && (
                          <div className="text-xs text-muted-foreground truncate mt-0.5">
                            {participant.email}
                          </div>
                        )}
                        {participant.deviceImei && (
                          <div className="text-xs text-muted-foreground truncate mt-0.5">
                            IMEI: {participant.deviceImei}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {getRoleDisplay(participant)}
                      <div className="flex items-center gap-1.5">
                        <div
                          className={`size-2 rounded-full ${isConnected ? "bg-green-500" : "bg-gray-400"
                            }`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {getConnectionStatus(participant)}
                        </span>
                      </div>
                    </div>

                    {participant.type === "device" && participant.device?.speakerVolume !== undefined && (
                      <div className="space-y-1">
                        <div className="text-xs text-muted-foreground">
                          Âm lượng loa
                        </div>
                        <Slider
                          value={[
                            volumeStates[participant.deviceImei || ""] ??
                            participant.device?.speakerVolume ??
                            0
                          ]}
                          max={100}
                          disabled={!isHostOrCoHost}
                          onValueChange={(value) =>
                            handleVolumeChange(
                              participant.deviceImei || "",
                              value[0]
                            )
                          }
                        />
                      </div>
                    )}

                    {meeting && isHostOrCoHost && !participant.deviceImei && <ParticipantResolutionSelect identity={participant.identity} disabled={!isConnected}/>}
                    {meeting && isHostOrCoHost && participant.deviceImei && <MeetingDeviceSettingsDialog meetingId={meeting.id} imei={participant.deviceImei}
                      name={participant.name || participant.identity} disabled={!isConnected} onSaved={refreshMeeting}/>}
                    {meeting && participant.deviceImei && <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">Âm lượng MIC</div>
                      <MeetingMicrophoneVolume meetingId={meeting.id} imei={participant.deviceImei}
                        name={participant.name || participant.identity}
                        volume={participant.device?.microphoneVolume ?? participant.meetingDevice?.microphoneVolume ?? 100}
                        disabled={!isHostOrCoHost || !isConnected} onSaved={refreshMeeting}/>
                    </div>}

                    {isHostOrCoHost &&
                      isConnected &&
                      participant.role !== "host" && (
                        <div className="flex flex-wrap gap-2 pt-2 border-t">
                          <Button
                            variant="ghost"
                            className={cn(
                              "flex-1 min-w-[100px] h-8 text-xs",
                              isMicEnabled
                                ? "border-green-700/50 bg-green-50 text-green-700"
                                : "border-gray-700/50 bg-gray-50 text-gray-700"
                            )}
                            size="sm"
                            onClick={() =>
                              isMicEnabled
                                ? handleMuteParticipant(participant)
                                : handleUnmuteParticipant(participant)
                            }
                            aria-label={isMicEnabled ? "Tắt mic" : "Bật mic"}
                            disabled={!isHostOrCoHost}
                          >
                            {isMicEnabled ? (
                              <>
                                <Mic className="size-3.5 text-green-500 mr-1" />
                                Tắt mic
                              </>
                            ) : (
                              <>
                                <MicOff className="size-3.5 text-gray-500 mr-1" />
                                Bật mic
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            className={cn(
                              "flex-1 min-w-[100px] h-8 text-xs",
                              isCameraEnabled
                                ? "border-green-700/50 bg-green-50 text-green-700"
                                : "border-gray-700/50 bg-gray-50 text-gray-700"
                            )}
                            size="sm"
                            onClick={() =>
                              isCameraEnabled
                                ? handleDisableCamera(participant)
                                : handleEnableCamera(participant)
                            }
                            aria-label={
                              isCameraEnabled ? "Tắt camera" : "Bật camera"
                            }
                            disabled={!isHostOrCoHost}
                          >
                            {isCameraEnabled ? (
                              <>
                                <Monitor className="size-3.5 text-green-500 mr-1" />
                                Tắt cam
                              </>
                            ) : (
                              <>
                                <VideoOff className="size-3.5 text-gray-500 mr-1" />
                                Bật cam
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            className="flex-1 min-w-[100px] h-8 text-xs border-gray-700/50 bg-gray-50 text-gray-700"
                            size="sm"
                            onClick={() => handleRemoveParticipant(participant)}
                            aria-label="Xóa khỏi cuộc họp"
                            disabled={!isHostOrCoHost}
                          >
                            <LogOut className="size-3.5 mr-1" />
                            Rời khỏi
                          </Button>
                          {/* Raise Hand Button */}

                          <Button
                            variant="ghost"
                            className={cn(
                              "flex-1 min-w-[100px] h-8 text-xs",
                              participant.raisedHand
                                ? "border-blue-700/50 bg-blue-50 text-blue-700"
                                : "border-gray-700/50 bg-gray-50 text-gray-700"
                            )}
                            size="sm"
                            onClick={() =>
                              handleRaiseHand(
                                participant,
                                participant.raisedHand ? "remove" : "add"
                              )
                            }
                            aria-label={
                              participant.raisedHand ? "Hạ tay" : "Giơ tay"
                            }
                            disabled={!isHostOrCoHost}
                          >
                            <Hand className="size-3.5 mr-1" />
                            {participant.raisedHand ? "Hạ tay" : "Giơ tay"}
                          </Button>

                          {/* Co-Host Button - Chỉ host mới có thể cấp/thu hồi quyền co-host */}
                          {
                            participant.role !== "host" &&
                            currentUserRole === "host" && (
                              <Button
                                variant="ghost"
                                className={cn(
                                  "flex-1 min-w-[100px] h-8 text-xs",
                                  participant.isCoHost
                                    ? "border-yellow-700/50 bg-yellow-50 text-yellow-700"
                                    : "border-gray-700/50 bg-gray-50 text-gray-700"
                                )}
                                size="sm"
                                onClick={() =>
                                  handleCoHost(
                                    participant,
                                    participant.isCoHost ? "remove" : "add"
                                  )
                                }
                                aria-label={
                                  participant.isCoHost
                                    ? "Thu hồi quyền đồng chủ trì"
                                    : "Cấp quyền đồng chủ trì"
                                }
                                disabled={!isHostOrCoHost}
                              >
                                <Crown className="size-3.5 mr-1" />
                                {participant.isCoHost
                                  ? "Bỏ đồng chủ trì"
                                  : "Đồng chủ trì"}
                              </Button>
                            )}
                          {/* Can Share Screen Button */}
                          {participant.type === "user" && (
                            <Button
                              variant="ghost"
                              className={cn(
                                "flex-1 min-w-[100px] h-8 text-xs",
                                participant.canShareScreen
                                  ? "border-green-700/50 bg-green-50 text-green-700"
                                  : "border-gray-700/50 bg-gray-50 text-gray-700"
                              )}
                              size="sm"
                              onClick={() =>
                                handleCanShareScreen(
                                  participant,
                                  participant.canShareScreen ? "remove" : "add"
                                )
                              }
                              disabled={!isHostOrCoHost}
                            >
                              {participant.canShareScreen
                                ? <>
                                  <ScreenShare className="size-3.5 mr-1" />
                                  Có thể chia sẻ
                                </>
                                :
                                <>
                                  <ScreenShareOff className="size-3.5 mr-1" />
                                  Cấm chia sẻ màn hình
                                </>
                              }

                            </Button>
                          )}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 pt-2 border-t">
                <div className="text-xs sm:text-sm text-muted-foreground">
                  Hiển thị {paginatedParticipants.length} /{" "}
                  {filteredParticipants.length} người tham gia
                </div>
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 sm:h-8 px-2 text-xs"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    aria-label="Trang đầu"
                  >
                    &lt;&lt;
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 sm:h-8 px-2 text-xs"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Trang trước"
                  >
                    &lt;
                  </Button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        className="h-7 sm:h-8 px-2 sm:px-3 text-xs"
                        onClick={() => setCurrentPage(page)}
                        aria-label={`Trang ${page}`}
                      >
                        {page}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 sm:h-8 px-2 text-xs"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    aria-label="Trang sau"
                  >
                    &gt;
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 sm:h-8 px-2 text-xs"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Trang cuối"
                  >
                    &gt;&gt;
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
